import React, { useState } from 'react';
import { Button, Card, Input, Tooltip, message, Drawer } from 'antd';
import { Link } from 'react-router-dom';
import Icon from '@components/Icon';
import SEO from '@components/seo/SEO';
import demoPng from '@assets/demo.png';
import { supportImg } from '@utils/utils';
import useSetImg from '@hooks/useSetImg';
import stores from '@stores';
import { safeLoadImageFromUrl } from '@utils/utils';

// 创建一个增强的图标封装组件，支持 lucide-react 和 Font Awesome
const SafeIcon = ({ icon: IconName, useFa = true, ...props }) => {
  // 如果传入的是图标名称字符串
  if (typeof IconName === 'string' && Icon[IconName]) {
    const IconComponent = Icon[IconName];
    return <IconComponent useFa={useFa} {...props} />;
  }
  
  // 如果传入的是组件
  if (typeof IconName === 'function' || typeof IconName === 'object') {
    return <IconName useFa={useFa} {...props} />;
  }
  
  // 如果没有有效的图标，则不渲染任何内容
  return null;
};

const LandingPage = ({ onStartEditing }) => {
  const [imageUrl, setImageUrl] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const getFile = useSetImg(stores);
  const [messageApi, contextHolder] = message.useMessage();

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      await getFile(file);
      if (onStartEditing) onStartEditing();
    }
  };

  const handleDemoClick = async () => {
    await getFile(demoPng, 'dataURL');
    if (onStartEditing) onStartEditing();
  };

  const handleUrlSubmit = async () => {
    if (!imageUrl.trim()) {
      messageApi.error('请输入图片URL');
      return;
    }

    console.log('开始加载图片URL:', imageUrl);
    messageApi.loading('正在加载图片，请稍候...');

    try {
      // 使用安全的图片加载函数
      const blob = await safeLoadImageFromUrl(imageUrl);
      console.log('获取到blob数据:', blob.type, blob.size);
      
      await getFile(blob);
      console.log('图片加载成功');
      messageApi.success('图片加载成功！');
      if (onStartEditing) onStartEditing();
    } catch (error) {
      console.error('图片加载错误:', error);
      messageApi.error(`无法加载图片: ${error.message || '请检查URL是否正确或图片是否允许跨域访问'}`);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-purple-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
      <SEO 
        title="图美啦 - 专业在线图片美化工具 | 一键生成精美图片效果"
        description="图美啦是一款专业的在线图片美化工具，支持一键为截图添加精美背景和标注，提供多种图形工具、裁剪、水印等实用功能，让您的图片展示效果更专业。"
        keywords="图片美化,截图美化,图片编辑器,在线图片工具,图片标注,图片裁剪,图片水印,图片背景,精美背景"
        canonicalUrl="https://snapbeautify.toolooz.com/"
      />
      {contextHolder}
      
      {/* 导航栏 */}
      <header className="bg-white dark:bg-gray-900 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <a href="https://snapbeautify.toolooz.com" className="flex items-center text-2xl font-bold text-purple-600 dark:text-purple-400">
                <img src="/favicon.svg" alt="图美啦" width="32" height="32" className="mr-2" />
                图美啦
              </a>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="font-medium text-purple-600 dark:text-purple-400 flex items-center">
                <SafeIcon icon="Home" size={16} className="mr-1" />
                首页
              </Link>
              <Link to="/video-capture" className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center">
                <SafeIcon icon="Video" size={16} className="mr-1" />
                视频截图
              </Link>
              <Link to="/quick-start" className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center">
                <SafeIcon icon="BookOpen" size={16} className="mr-1" />
                快速上手
              </Link>
              <Link to="/about" className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center">
                <SafeIcon icon="User" size={16} className="mr-1" />
                关于我们
              </Link>
            </nav>
            <div className="flex md:hidden">
              <button 
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                onClick={() => setMobileMenuOpen(true)}
              >
                <SafeIcon icon="Menu" size={24} />
              </button>
            </div>
          </div>
        </div>
        
        {/* 移动设备导航菜单 */}
        <Drawer
          title="导航菜单"
          placement="right"
          onClose={() => setMobileMenuOpen(false)}
          open={mobileMenuOpen}
          width={250}
        >
          <div className="flex flex-col space-y-4">
            <Link 
              to="/" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="Home" size={16} className="mr-1" />
              首页
            </Link>
            <Link 
              to="/video-capture" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="Video" size={16} className="mr-1" />
              视频截图
            </Link>
            <Link 
              to="/quick-start" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="BookOpen" size={16} className="mr-1" />
              快速上手
            </Link>
            <Link 
              to="/about" 
              className="font-medium text-gray-700 hover:text-purple-600 dark:text-gray-300 dark:hover:text-purple-400 flex items-center py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <SafeIcon icon="User" size={16} className="mr-1" />
              关于我们
            </Link>
          </div>
        </Drawer>
      </header>

      {/* 主要内容区域 */}
      <main className="flex-grow container mx-auto px-4 py-8">
        {/* 英雄区域 */}
        <div className="text-center mb-16 mt-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-800 dark:text-white mb-4">
            让您的<span className="text-purple-600 dark:text-purple-400">图片</span>更加精美
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
            一站式图片美化工具，轻松添加精美背景和标注，带来专业级图片展示效果
          </p>
          
          {/* 上传图片卡片 */}
          <div className="max-w-3xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden mb-12">
            <div className="md:flex">
              <div className="md:flex-1 p-8">
                <div className="bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30 p-6 rounded-lg mb-6">
                  <div className="text-center">
                    <SafeIcon icon="ImagePlus" className="mx-auto text-purple-600 dark:text-purple-400" size={48} />
                    <h3 className="mt-4 text-xl font-semibold text-gray-800 dark:text-white">开始美化您的图片</h3>
                    <p className="mt-2 text-gray-600 dark:text-gray-300">上传图片或粘贴URL，我们将为您的图片添加精美效果</p>
                  </div>
                </div>
                
                <div className="flex flex-col gap-4">
                  <div>
                    <label htmlFor="file-upload" className="flex items-center justify-center w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg cursor-pointer transition duration-200">
                      <SafeIcon icon="Upload" size={20} className="mr-2" />
                      本地上传图片
                    </label>
                    <input
                      id="file-upload"
                      type="file"
                      accept={supportImg.join(',')}
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="输入图片URL"
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      prefix={<SafeIcon icon="Link" size={16} className="text-gray-400" />}
                      className="flex-grow"
                    />
                    <Button
                      type="primary"
                      onClick={handleUrlSubmit}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      获取
                    </Button>
                  </div>
                  
                  <Button
                    type="default"
                    onClick={handleDemoClick}
                    className="border border-dashed border-gray-300 hover:border-purple-500 hover:text-purple-600 dark:border-gray-600 dark:hover:border-purple-400 dark:hover:text-purple-400"
                  >
                    <div className="flex items-center">
                      <div className="w-5 h-5 mr-2 overflow-hidden rounded-sm">
                        <img src={demoPng} alt="示例" className="w-full h-full object-cover" />
                      </div>
                      <span>试用示例</span>
                    </div>
                  </Button>
                </div>
              </div>
              
              <div className="md:flex-1 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 p-8 flex items-center justify-center">
                <div className="relative w-full max-w-md">
                  <img src="/device-showcase.svg" alt="图片展示" className="w-full h-auto" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 功能介绍区域 */}
        <div id="guide" className="py-8">
          <h2 className="text-3xl font-bold text-center text-gray-800 dark:text-white mb-12">
            <SafeIcon icon="Sparkles" className="inline-block mr-2 text-yellow-500" size={28} />
            强大的功能
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* 功能卡片 1 */}
            <Card className="hover:shadow-lg transition-shadow duration-300 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-gray-800 dark:to-gray-700 border-0">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                  <SafeIcon icon="Layers" size={32} useFa={true} />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-2 text-gray-800 dark:text-white">智能布局</h3>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                智能调整图片布局和排版，自动适应各种展示场景，让您的内容呈现更加专业。
              </p>
            </Card>

            {/* 视频截图功能卡片 */}
            <Card className="hover:shadow-lg transition-shadow duration-300 bg-gradient-to-br from-green-50 to-green-100 dark:from-gray-800 dark:to-gray-700 border-0">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                  <SafeIcon icon="Video" size={32} />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-2 text-gray-800 dark:text-white">视频截图</h3>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                轻松从视频中捕获高质量截图，并进行编辑、美化和导出，让视频内容变为精美图片。
                <Link to="/video-capture" className="block mt-2 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300">
                  立即尝试 →
                </Link>
              </p>
            </Card>

            {/* 功能卡片 2 */}
            <Card className="hover:shadow-lg transition-shadow duration-300 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-gray-800 dark:to-gray-700 border-0">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                  <SafeIcon icon="Palette" size={32} />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-2 text-gray-800 dark:text-white">精美背景</h3>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                多种精美背景和渐变色供您选择，瞬间提升您的图片视觉效果，让您的内容更加吸引人。
              </p>
            </Card>

            {/* 功能卡片 3 */}
            <Card className="hover:shadow-lg transition-shadow duration-300 bg-gradient-to-br from-pink-50 to-pink-100 dark:from-gray-800 dark:to-gray-700 border-0">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400">
                  <SafeIcon icon="Edit3" size={32} />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-2 text-gray-800 dark:text-white">标注工具</h3>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                强大的标注功能，支持箭头、矩形、圆形等多种标注形状，帮助您更好地解释和突出重点内容。
              </p>
            </Card>

            {/* 功能卡片 4 */}
            <Card className="hover:shadow-lg transition-shadow duration-300 bg-gradient-to-br from-green-50 to-green-100 dark:from-gray-800 dark:to-gray-700 border-0">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                  <SafeIcon icon="Crop" size={32} />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-2 text-gray-800 dark:text-white">裁剪与调整</h3>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                轻松裁剪和调整图片尺寸，适应各种显示需求，支持多种社交媒体平台的最佳尺寸。
              </p>
            </Card>

            {/* 功能卡片 5 */}
            <Card className="hover:shadow-lg transition-shadow duration-300 bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-gray-800 dark:to-gray-700 border-0">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400">
                  <SafeIcon icon="Download" size={32} />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-2 text-gray-800 dark:text-white">多格式导出</h3>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                支持 PNG、JPG、WebP 等多种格式导出，满足不同场景的需求，一键复制分享您的作品。
              </p>
            </Card>

            {/* 功能卡片 6 */}
            <Card className="hover:shadow-lg transition-shadow duration-300 bg-gradient-to-br from-red-50 to-red-100 dark:from-gray-800 dark:to-gray-700 border-0">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                  <SafeIcon icon="Zap" size={32} useFa={true} />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-2 text-gray-800 dark:text-white">简单快速</h3>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                简洁直观的界面设计，无需专业设计知识，轻松几步即可创建出精美的图片作品。
              </p>
            </Card>
            
            {/* 功能卡片 7 - 新增 */}
            <Card className="hover:shadow-lg transition-shadow duration-300 bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-gray-800 dark:to-gray-700 border-0">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                  <SafeIcon icon="Shield" size={32} useFa={true} />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-2 text-gray-800 dark:text-white">安全可靠</h3>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                所有处理在本地进行，无需担心隐私泄露，您的图片不会上传到服务器，保障您的数据安全。
              </p>
            </Card>

            {/* 功能卡片 8 - 新增 */}
            <Card className="hover:shadow-lg transition-shadow duration-300 bg-gradient-to-br from-teal-50 to-teal-100 dark:from-gray-800 dark:to-gray-700 border-0">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400">
                  <SafeIcon icon="Globe" size={32} useFa={true} />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-2 text-gray-800 dark:text-white">免费在线</h3>
              <p className="text-gray-600 dark:text-gray-300 text-center">
                完全免费使用，无需下载安装任何软件，只需浏览器即可随时随地进行图片美化和编辑。
              </p>
            </Card>
          </div>
        </div>

        {/* 关于我们区域 */}
        <div id="about" className="py-16">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
            <div className="p-8">
              <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-6 flex items-center">
                <SafeIcon icon="Users" className="mr-2 text-blue-600 dark:text-blue-400" size={28} />
                关于我们
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                图美啦是一款在线图片美化工具，致力于为用户提供简单、快速、高效的图片处理解决方案。我们的目标是帮助用户轻松创建出专业级别的图片展示效果，无论您是产品经理、设计师、开发者还是普通用户，都能通过我们的工具展示出更加精美的图片内容。
              </p>
              <p className="text-gray-600 dark:text-gray-300">
                我们持续优化产品功能和用户体验，欢迎提供宝贵意见和建议，一起让图片处理变得更加简单和有趣！
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* 页脚区域 */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <SafeIcon icon="Info" size={18} className="mr-2 text-blue-400" />
                关于我们
              </h3>
              <p className="text-gray-400 text-sm">
                图美啦是一款专业的在线图片美化工具，致力于为用户提供简单、高效的图片处理解决方案。
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <SafeIcon icon="FileText" size={18} className="mr-2 text-purple-400" />
                使用条款
              </h3>
              <ul className="text-gray-400 text-sm space-y-2">
                <li><a href="/privacy" className="hover:text-white transition-colors">隐私政策</a></li>
                <li><a href="/terms" className="hover:text-white transition-colors">使用条款</a></li>
                <li><a href="/copyright" className="hover:text-white transition-colors">版权声明</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <SafeIcon icon="BookOpen" size={18} className="mr-2 text-green-400" />
                快速上手
              </h3>
              <ul className="text-gray-400 text-sm space-y-2">
                <li><a href="/quick-start" className="hover:text-white transition-colors">快速上手</a></li>
                <li><a href="/faq" className="hover:text-white transition-colors">常见问题</a></li>
                <li><a href="/features" className="hover:text-white transition-colors">功能介绍</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <SafeIcon icon="MessageSquare" size={18} className="mr-2 text-yellow-400" />
                联系我们
              </h3>
              <ul className="text-gray-400 text-sm space-y-2">
                <li className="flex items-center">
                  <SafeIcon icon="AtSign" size={16} className="mr-2 text-blue-400" />
                  <Tooltip title="点击复制微信号" placement="left">
                    <button 
                      className="hover:text-white transition-colors"
                      onClick={() => {
                        navigator.clipboard.writeText('liqidashi2013');
                        message.success('微信号已复制到剪贴板');
                      }}
                    >
                      微信：liqidashi2013
                    </button>
                  </Tooltip>
                </li>
                <li className="flex items-center">
                  <SafeIcon icon="Share2" size={16} className="mr-2 text-red-400" />
                  <a href="https://weibo.com/u/3937088387" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                    微博：weibo.com/u/3937088387
                  </a>
                </li>
                <li className="flex items-center">
                  <SafeIcon icon="MessageCircle" size={16} className="mr-2 text-green-400" />
                  <Tooltip title="点击复制QQ号" placement="left">
                    <button 
                      className="hover:text-white transition-colors"
                      onClick={() => {
                        navigator.clipboard.writeText('2316757226');
                        message.success('QQ号已复制到剪贴板');
                      }}
                    >
                      QQ：2316757226
                    </button>
                  </Tooltip>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>© {new Date().getFullYear()} 图美啦 - 让图片更美丽 | SnapBeautify</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage; 